import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel } from '../../platform/primitives/Panel'
import { RECONNECT_EXPIRED_TITLE, RECONNECT_NOT_RESUMED_TITLE, RECONNECT_UNUSABLE_BODY } from './relinkOutcome'
import { useToast } from '../../platform/feedback/ToastProvider'
import { usePlaidOAuthSession } from './providers/plaidOAuth'
import { emit } from '../../platform/analytics/analytics'
import { reconnectOutcomeEvent, usePlaidOAuthResume } from './useAggregator'
import { useApplyRelinkOutcome } from './useRelinkOutcome'
import './accounts.css'
import '../../platform/notes.css'

/**
 * `/plaid` — #414. Reached by a COLD page load: an OAuth institution makes
 * Plaid's Link UI navigate the browser fully away to the bank and back,
 * destroying every in-memory value `Connect.tsx`/`Connections.tsx` had.
 * This screen's only job is to pick the flow back up with the `link_token`
 * `providers/plaidOAuth.ts` persisted before that navigation, passing
 * `receivedRedirectUri` so Plaid Link RESUMES the same session instead of
 * starting a fresh one — `usePlaidOAuthResume` (`useAggregator.ts`) does the
 * actual resume-and-complete work; this component only reads the pending
 * session once and renders whatever it reports.
 *
 * **Inside `RequireAuth`/`ChromeLayout` (`App.tsx`), not bare.** The user is
 * genuinely authenticated at this point — the Firebase session survives a
 * same-origin navigation — and a failure here benefits from the sidebar the
 * way `ACCOUNT_CONNECT_PATH` does, unlike `FLINKS_CALLBACK_PATH`'s popup
 * (shares the opener's session and posts one message before closing, so it
 * never needed a session of its own). `RequireAuth` already renders
 * `BootIndicator` for a cold boot's `unknown`/`bootstrapping` window, so
 * this screen never mounts before there is an answer either way.
 *
 * **No stored session is a real, handled state — not a crash.** A stale
 * bookmark, a direct visit, or a second tab all read the same key and find
 * it already consumed (`usePendingPlaidOAuth`'s "read once and clear").
 *
 * **A pending session with no `oauth_state_id` on the URL is treated the
 * same way** (security review, #414, SEC-2). A stored `link_token` alone is
 * not proof Plaid actually sent the browser back — nothing stops a user
 * mid-flow from being induced to load `/plaid` directly with crafted query
 * parameters — so the resume only fires when the URL carries the query
 * parameter a real OAuth return always does. The token is still consumed
 * either way (`usePendingPlaidOAuth` already ran by the time this is
 * checked), so a later, LEGITIMATE visit can never replay it.
 *
 * Read off `useLocation()`, not `window.location` directly — the same
 * convention `RequireAuth.tsx` already uses, and the one that makes this
 * checkable under `MemoryRouter` in a test: `BrowserRouter` (production)
 * syncs its `location` from the real URL on a cold load, and `window
 * .location` itself never changes under a router that manages its own
 * in-memory history.
 */
function hasOAuthStateId(search: string): boolean {
  return new URLSearchParams(search).has('oauth_state_id')
}

export function PlaidOAuthReturn() {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const session = usePlaidOAuthSession()
  const pending = session.status === 'pending' ? session.pending : null
  const resumeFlow = usePlaidOAuthResume()
  const { apply, dialogs } = useApplyRelinkOutcome()

  // The session is CONSUMED regardless (the hook above already did that),
  // but only USED when the URL looks like a real Plaid return — see the
  // component header's SEC-2 note.
  const usable = pending !== null && hasOAuthStateId(location.search) ? pending : null

  // Guards the ONE real side effect this screen has (mounting Plaid's own
  // Link UI) against firing twice — `<StrictMode>` (`main.tsx`) double-runs
  // an effect in development, and refs, unlike the effect body itself,
  // persist across that synthetic remount (same guard shape `returnTo.ts`'s
  // own header documents for the identical hazard).
  const started = useRef(false)
  useEffect(() => {
    if (usable === null || started.current) return
    started.current = true
    resumeFlow.resume(usable)
  }, [usable, resumeFlow])

  // A reconnect that outlived its nonce is a failed reconnect the user never saw
  // through. The entry was consumed by the read above, so a reload finds nothing;
  // the ref covers StrictMode's second effect pass. The provider is not trusted
  // from storage here, so none is reported.
  const expiredReported = useRef(false)
  const expiredEntry = session.status === 'expired_relink' ? session.entryPoint : undefined
  const isExpired = session.status === 'expired_relink'
  useEffect(() => {
    if (!isExpired || expiredReported.current) return
    expiredReported.current = true
    const event = reconnectOutcomeEvent({
      provider: null,
      entryPoint: expiredEntry,
      startedAt: undefined,
      outcome: { status: 'failed', kind: 'nonce_invalid' },
    })
    emit(event.name, event.props)
  }, [isExpired, expiredEntry])

  useEffect(() => {
    if (resumeFlow.state.status !== 'success') return
    const relinked = resumeFlow.relinked
    if (usable?.kind === 'relink' && relinked !== null) {
      // A reconnect: the ONE status-to-copy mapping (`relinkOutcome.ts`) decides
      // what to say and where to go — this screen keeps no table of its own.
      // Whatever it says without navigating, the user then lands on
      // Connections, where the new connection shows. The bank the user meant is
      // not known here (the return has no list to look it up in), so the copy
      // names no bank.
      const outcome = apply(
        {
          kind: 'started',
          newItemId: relinked.newItemId,
          relinkStatus: relinked.relinkStatus,
          institutionName: relinked.institutionName,
        },
        {
          bank: null,
          provider: null,
          replace: true,
          onDifferentBankClose: () => navigate('/connections', { replace: true }),
        },
      )
      if (outcome.kind === 'toast' || outcome.kind === 'silent') navigate('/connections', { replace: true })
      return
    }
    toast.show({ kind: 'success', message: 'Account connected.' })
    navigate(usable?.kind === 'reauth' ? '/connections' : '/accounts', { replace: true })
  }, [resumeFlow.state.status, resumeFlow.relinked, toast, navigate, usable, apply])

  // A reconnect whose session cannot be used — it outlived the server's nonce, or
  // the URL is not a real Plaid return. NEVER "Add an account": the bank is
  // still disconnected, and adding it fresh creates the duplicate connection a
  // reconnect exists to replace. The way out is the reconnect on Connections.
  if (usable === null && (session.status === 'expired_relink' || pending?.kind === 'relink')) {
    return (
      <Panel className="accounts-stack">
        <h2 className="accounts-total__label">
          {session.status === 'expired_relink'
            ? RECONNECT_EXPIRED_TITLE
            : RECONNECT_NOT_RESUMED_TITLE}
        </h2>
        <p className="note">
          {RECONNECT_UNUSABLE_BODY}
        </p>
        <InlineLink to="/connections">Back to connections</InlineLink>
      </Panel>
    )
  }

  if (usable === null) {
    return (
      <Panel className="accounts-stack">
        <h2 className="accounts-total__label">This connection window has expired</h2>
        <p className="note">
          We couldn’t find a bank connection in progress on this browser tab. Start again from Accounts.
        </p>
        <InlineLink to="/accounts/connect">Add an account</InlineLink>
      </Panel>
    )
  }

  // Explicit per status (N3, architect review) rather than a fall-through
  // default: `LinkFlowState` is a closed union of five members, and naming
  // each one means a status added to it later fails typechecking here
  // instead of silently rendering "Finishing…" forever.
  switch (resumeFlow.state.status) {
    case 'manual_fallback':
      return (
        <Panel className="accounts-stack">
          <h2 className="accounts-total__label">The connection could not be completed</h2>
          <p role="alert" className="note note--stale">
            {resumeFlow.state.message}
          </p>
          {usable.kind === 'relink' ? (
            <InlineLink to="/connections">Back to connections</InlineLink>
          ) : (
            <InlineLink to="/accounts/connect">Add an account</InlineLink>
          )}
        </Panel>
      )
    case 'not_repaired':
      return (
        <Panel className="accounts-stack">
          <h2 className="accounts-total__label">The connection could not be repaired</h2>
          <p className="note">You can try reconnecting again from Connections.</p>
          <InlineLink to="/connections">Back to connections</InlineLink>
        </Panel>
      )
    case 'idle':
    case 'pending':
    case 'success':
      // `success` still renders this for the one tick before the effect
      // above navigates away — never long enough to be its own case.
      return (
        <Panel className="accounts-stack">
          <h2 className="accounts-total__label">Finishing your connection…</h2>
          {/* `aria-live="polite"`, not `role="alert"` — this is progress, not
              an interruption, and `role="alert"`'s assertive announcement is
              reserved for the error states above. A screen-reader user gets
              told work is happening on a screen whose entire first paint
              otherwise says nothing (N3, architect review). */}
          <p className="note" aria-live="polite">
            Hold on while we reconnect to your bank.
          </p>
          {dialogs}
        </Panel>
      )
  }
}
